import { Template, LocalizedTemplate, TemplateWithoutBody } from "../model/Template";
import { TemplateEmailPreview } from "../model/TemplateEmailPreview";
import { fetchWithToken } from "./fetchWithToken";
import {
  templateFromBackendFormat,
  templateToBackendFormat,
} from "../util/TemplateTransformations";

const getBaseUrl = (customerId: string) => {
  let url;
  switch (process.env.NODE_ENV) {
    case "production":
      url = `/api/customers/${customerId}/templates`;
      break;
    case "development":
      url = `http://localhost:5000/api/customers/${customerId}/templates`;
      break;
    default:
      url = `/api/customers/${customerId}/templates`;
  }

  return url;
};

/**
 * Get template for admin editing (with all localizations)
 */
export async function getTemplate(
  templateId: string,
  customerId: string
): Promise<Template> {
  try {
    const response = await fetchWithToken(
      getBaseUrl(customerId) + `/${templateId}`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      throw new Error("Error while loading template");
    }

    const template = await response.json();
    return templateFromBackendFormat(template);
  } catch (error) {
    throw new Error("Error while loading template");
  }
}

/**
 * Get localized template for user display (single language)
 */
export async function getLocalizedTemplate(
  templateId: string,
  customerId: string
): Promise<LocalizedTemplate> {
  try {
    const response = await fetchWithToken(
      getBaseUrl(customerId) + `/${templateId}/localized`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      throw new Error("Error while loading template");
    }

    return await response.json();
  } catch (error) {
    throw new Error("Error while loading template");
  }
}

export async function getTemplateLearningPagePreview(
  template_id: string,
  customerId: string
) {
  try {
    const response = await fetchWithToken(
      getBaseUrl(customerId) + `/${template_id}/learningpage`,
      {
        method: "GET",
      }
    );
    return response;
  } catch (error) {
    throw new Error("Error while loading template");
  }
}

export async function getTemplateEmailPreview(
  templateId: string,
  customerId: string
): Promise<TemplateEmailPreview> {
  try {
    const response = await fetchWithToken(
      getBaseUrl(customerId) + `/${templateId}/emailpreview`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      throw new Error("Error while loading template email preview");
    }

    return await response.json();
  } catch (error) {
    throw new Error("Error while loading template email preview");
  }
}

export async function getCustomerTemplatesForUsers(
  customerId: string
): Promise<TemplateWithoutBody[]> {
  try {
    const response = await fetchWithToken(getBaseUrl(customerId), {
      method: "GET",
    });

    if (!response.ok) {
      throw new Error("Error while loading user templates");
    }

    return await response.json();
  } catch (error) {
    throw new Error("Error while loading user templates");
  }
}

export async function getCustomerTemplatesForAdmins(
  customerId: string
): Promise<TemplateWithoutBody[]> {
  try {
    let url;
    switch (process.env.NODE_ENV) {
      case "production":
        url = `/api/system/customers/${customerId}/templates`;
        break;
      case "development":
        url = `http://localhost:5000/api/system/customers/${customerId}/templates`;
        break;
      default:
        url = `/api/system/customers/${customerId}/templates`;
    }
    
    const response = await fetchWithToken(url, {
      method: "GET",
    });

    if (!response.ok) {
      throw new Error("Error while loading admin templates");
    }

    return await response.json();
  } catch (error) {
    throw new Error("Error while loading admin templates");
  }
}

export async function listDemoTemplates(customerId: string) {
  try {
    const response = await fetchWithToken(getBaseUrl(customerId) + "/demo", {
      method: "GET",
    });
    return response;
  } catch (error) {
    throw new Error("Error while loading template");
  }
}

export async function createTemplate(template: Template, customerId: string) {
  const sendTemp = templateToBackendFormat(template);
  try {
    const response = await fetchWithToken(getBaseUrl(customerId), {
      method: "POST",
      body: JSON.stringify(sendTemp),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw new Error("Error while loading template");
  }
}

export async function copyTemplatesToCustomer(
  templateIds: string[],
  targetCustomerId: string
) {
  try {
    const response = await fetchWithToken(
      process.env.REACT_APP_API_URL +
        `/system/templates/clone?targetCustomer=${targetCustomerId}`,
      {
        method: "POST",
        body: JSON.stringify(templateIds),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    throw new Error("Error while loading template");
  }
}

export async function editTemplate(
  template: Template,
  template_id: string,
  customerId: string
) {
  const sendTemp = templateToBackendFormat(template);
  try {
    const response = await fetchWithToken(
      getBaseUrl(customerId) + `/${template_id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sendTemp),
      }
    );
    return response;
  } catch (error) {
    throw new Error("Error while loading template");
  }
}

export async function updateDemoTemplates(
  templateIds: string[],
  customerId: string
) {
  try {
    const response = await fetchWithToken(getBaseUrl(customerId) + `/demo`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(templateIds),
    });
    return response;
  } catch (error) {
    throw new Error("Error while loading template");
  }
}

export async function deleteTemplate(template_id: string, customerId: string) {
  try {
    const response = await fetchWithToken(
      getBaseUrl(customerId) + `/${template_id}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    throw new Error("Error while loading template");
  }
}

export async function deactivateTemplate(
  template_id: string,
  customerId: string
) {
  try {
    const response = await fetchWithToken(
      getBaseUrl(customerId) + `/${template_id}/deactivate`,
      {
        method: "PUT",
      }
    );
    return response;
  } catch (error) {
    throw new Error("Error");
  }
}
